// MIDAS V3 — Step-receipt row (SP-1, PLUMBING PASS)
//
// One-line gray "step receipt" row for the v3 segment-streaming flow. Rendered
// interleaved with prose blocks in wire order (§10.2):
//   • settled row (default)  — a completed step, from a marker line in the
//     token stream / final answer body (`markerBlocksV3.splitMarkerBlocks`).
//   • live row (`live` prop) — the in-progress round, from `loop_progress`
//     `stage` frames (SP-2). Adds a subtle ellipsis animation (NO spinner).
//
// Content is LABEL TEXT ONLY — no counts, no spinner, no red, no icon/border/
// background-fill/chevron (§10.3). Non-expandable in this sub-phase (expansion
// = SP-5, gated on `step_end.checks`).
//
// ⚠ VISUAL TREATMENT HERE IS DELIBERATELY PLAIN/FUNCTIONAL. The reference-
// matched final look (Claude-style muted gray receipt rows, §1A reference
// images) lands in SP-1V — the Creative Designer restyles this component + the
// chrome around it. Do NOT polish here; just build clean structure/props.
//
// Reduced-motion: the `live` ellipsis uses the shared `midas-pulse` keyframe,
// which the global `prefers-reduced-motion` override in midas-v3-tokens.scss
// neutralizes (animation-duration: 0.001ms). No height animation.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

export interface StepRowV3Props {
  /** Plain-text row label (bot-composed, already in thread language — never
   *  translated FE-side, never markdown/chip-processed). */
  label: string;
  /** Live (in-progress) variant — adds the subtle ellipsis animation. */
  live?: boolean;
}

const StepRowV3: React.FC<StepRowV3Props> = ({ label, live = false }) => {
  return (
    <div
      className="midas-v3-step-row"
      data-live={live ? "true" : undefined}
      style={{
        display: "flex",
        // baseline so the live ellipsis sits on the first line when the label
        // wraps to two lines on a narrow (phone) column.
        alignItems: "baseline",
        gap: 6,
        // SP-1V: heading-like rhythm — a clear gap ABOVE separates the row from
        // the preceding prose/turn, a snug gap BELOW ties it to the block it
        // introduces (matches the §1A reference cadence). Reduced from a big
        // opener gap so a turn that OPENS with a live row sits close under the
        // Midas header strip.
        margin: "16px 0 6px",
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        lineHeight: 1.45,
        // Muted (not faint) keeps the receipt readable while clearly quieter
        // than the near-black prose — the reference's light-gray step lines.
        color: "var(--muted)",
      }}
    >
      <span
        style={{
          // Wrap gracefully on narrow columns rather than clip mid-word — a
          // step receipt reads fine on two lines; a clipped one does not.
          minWidth: 0,
          wordBreak: "break-word",
        }}
      >
        {label}
      </span>
      {live && (
        <span
          aria-hidden="true"
          className="midas-v3-step-row-ellipsis mono"
          style={{
            flexShrink: 0,
            color: "var(--faint)",
            animation: "midas-pulse 1.4s ease-in-out infinite",
          }}
        >
          …
        </span>
      )}
    </div>
  );
};

export default StepRowV3;
